import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import {
    Device,
    DeviceFilterOptionsResponse,
    DeviceQueryParams,
    DeviceUpdate,
    PaginatedDeviceResponse,
} from '../types/device'
import { clearDeviceMosquitoCache } from './deviceMosquitoService'
import { clearHistoryCache } from './historyService'
import { clearStatMosquitoCache } from './statMosquitoService'
import { languageService } from './localeService'
import { normalizeSearchText } from '../utils/searchText'

const joinCsvValues = (values?: number[]): string | undefined =>
    values && values.length > 0 ? values.join(',') : undefined

const downloadBlob = (blob: Blob, filename: string) => {
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
}

const parseContentDispositionFilename = (
    dispositionHeader?: string
): string | undefined => {
    if (!dispositionHeader) {
        return undefined
    }

    const encodedMatch = dispositionHeader.match(/filename\*=UTF-8''([^;]+)/i)
    if (encodedMatch?.[1]) {
        try {
            return decodeURIComponent(encodedMatch[1])
        } catch (error) {
            console.warn('Failed to decode device export filename:', error)
        }
    }

    const plainMatch = dispositionHeader.match(/filename="?([^"]+)"?/i)
    return plainMatch?.[1]
}

/**
 * 获取所有设备列表
 */
export const getAllDevices = async (): Promise<Device[]> => {
    try {
        const response = await api.get('/device-mosquito/devices')
        return response.data
    } catch (error) {
        console.error('获取设备列表失败:', error)
        throw error
    }
}

export const queryDevices = async (
    params: DeviceQueryParams
): Promise<PaginatedDeviceResponse> => {
    try {
        const response = await api.get('/device-mosquito/devices', {
            params: {
                page: params.page,
                page_size: params.page_size,
                search_name:
                    normalizeSearchText(params.search_name) || undefined,
                project_ids: joinCsvValues(params.project_ids),
                region_ids: joinCsvValues(params.region_ids),
                status:
                    params.status && params.status !== 'all'
                        ? params.status
                        : undefined,
                lure_filter:
                    params.lure_filter && params.lure_filter !== 'all'
                        ? params.lure_filter
                        : undefined,
                purchase_version: params.purchase_version || undefined,
                cloud_service_status:
                    params.cloud_service_status &&
                    params.cloud_service_status !== 'all'
                        ? params.cloud_service_status
                        : undefined,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
            },
        })
        return response.data
    } catch (error) {
        console.error('查询设备列表失败:', error)
        throw error
    }
}

export const getDeviceFilterOptions =
    async (): Promise<DeviceFilterOptionsResponse> => {
        try {
            const response = await api.get('/device-mosquito/devices/filter-options')
            return response.data
        } catch (error) {
            console.error('获取设备筛选选项失败:', error)
            throw error
        }
    }

export const exportDevices = async (params: {
    project_ids?: number[]
    region_ids?: number[]
    status?: 'all' | 'online' | 'offline'
    lure_filter?: 'all' | 'expiring_1_days' | 'expiring_3_days' | 'expiring_7_days' | 'expiring_15_days' | 'expired'
    purchase_version?: string
    cloud_service_status?: 'all' | 'unknown' | 'normal' | 'expiring' | 'overdue'
}): Promise<void> => {
    const response = await api.get('/device-mosquito/devices/export', {
        params: {
            project_ids: joinCsvValues(params.project_ids),
            region_ids: joinCsvValues(params.region_ids),
            status: params.status && params.status !== 'all' ? params.status : undefined,
            lure_filter:
                params.lure_filter && params.lure_filter !== 'all'
                    ? params.lure_filter
                    : undefined,
            purchase_version: params.purchase_version || undefined,
            cloud_service_status:
                params.cloud_service_status &&
                params.cloud_service_status !== 'all'
                    ? params.cloud_service_status
                    : undefined,
            lang: languageService.getCurrentLanguage(),
        },
        responseType: 'blob',
    })

    const blob = new Blob([response.data], {
        type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const fileName = parseContentDispositionFilename(
        response.headers['content-disposition']
    )
    downloadBlob(
        blob,
        fileName ||
            `device_management_${new Date().toISOString().split('T')[0]}.xlsx`
    )
}

/**
 * 获取单个设备信息
 */
export const getDevice = async (deviceId: string): Promise<Device> => {
    try {
        const response = await api.get(`/device-mosquito/devices/${deviceId}`)
        return response.data
    } catch (error) {
        console.error('获取设备信息失败:', error)
        throw error
    }
}

/**
 * 更新设备信息
 */
export const updateDevice = async (
    deviceId: string,
    deviceUpdate: DeviceUpdate,
    options?: MutationRequestOptions
): Promise<Device> => {
    try {
        const response = await api.put(
            `/device-mosquito/devices/${deviceId}`,
            deviceUpdate,
            getMutationRequestConfig(options)
        )
        clearDeviceMosquitoCache()
        clearHistoryCache()
        clearStatMosquitoCache()
        return response.data
    } catch (error) {
        console.error('更新设备失败:', error)
        throw error
    }
}

export const uploadDeviceInstallationImage = async (
    deviceId: string,
    file: File
): Promise<{ object_key: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<{ object_key: string }>(
        `/device-mosquito/devices/${deviceId}/installation-image`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            preventDuplicate: false, // mutation-policy: allow-concurrent upload requests
        }
    )
    return response.data
}

/**
 * 删除设备
 */
export const deleteDevice = async (
    deviceId: string,
    options?: MutationRequestOptions
): Promise<{ message: string; device_id: string }> => {
    try {
        const response = await api.delete(
            `/device-mosquito/devices/${deviceId}`,
            getMutationRequestConfig(options)
        )
        clearDeviceMosquitoCache()
        clearHistoryCache()
        clearStatMosquitoCache()
        return response.data
    } catch (error) {
        console.error('删除设备失败:', error)
        throw error
    }
}
